import { z } from 'zod';
import {
  DOC_TYPE_CODES, OCCUPANCY_CODES, LOAN_PURPOSE_CODES, CITIZENSHIP_CODES,
  PROPERTY_TYPE_CODES, CREDIT_EVENT_CODES, US_STATES, isCashOut,
  unitsForPropertyType,
  type DocTypeCode, type LoanPurpose, type Occupancy,
} from './vocabulary.ts';

/**
 * The intake form — the "10 questions" that act as the top-level filter, plus the
 * free-text "what is funky about your file?" box.
 *
 * Every numeric field is a real `number` here. The prototype stored all of them as
 * comma-formatted strings ("637,500"), which is a presentation concern; parsing
 * happens at the form boundary so nothing downstream ever sees a formatted string.
 *
 * EVERY FIELD IS OPTIONAL, and that is a product decision rather than a loosening.
 * Henry, 2026-08-27: "on our GUI, none of the pulldown fields need to be required
 * fields, they should all be optional." A broker pricing a file at nine in the morning
 * has the loan size and the story; the score and the doc type arrive later, and a form
 * that refuses to search without them is a form that gets abandoned.
 *
 * What makes that safe is what happens downstream, not here: a null field WIDENS the
 * grid sweep instead of eliminating from it (see evaluate.ts), and the answer is made
 * to say which fields it widened on (see answer.ts). A schema that accepts a blank and
 * a query that matches nothing on it would produce a confident "no lender can do this"
 * out of an untouched dropdown, which is the worst answer this product can give and the
 * one that would look most like a real one.
 *
 * Two fields resist that treatment and are handled explicitly rather than silently:
 *   - propertyValue and loanAmount are nullable here, but without BOTH there is no LTV,
 *     and LTV is the axis of every grid. missingLtvInputs() names that case so a caller
 *     can refuse it in words instead of returning an empty result set.
 *   - creditEventSeasoningMonths stays nullable, and the form still requires it when an
 *     event is selected. An undated event is not a blank dropdown - the broker has
 *     asserted the event exists - and the evaluator already answers it explicitly
 *     rather than quietly ("the file does not say when the event was").
 */

const enumOf = <T extends string>(codes: readonly T[]) =>
  z.enum(codes as unknown as [T, ...T[]]);

/** FICO 300-850. 0 is meaningful in NonQM — several matrices have a "NO CREDIT SCORE" grid. */
export const ficoSchema = z.union([
  z.literal(0),
  z.number().int().min(300).max(850),
]);

/**
 * Optional the same way on every field: null is the stored absence, and `undefined` from
 * a form that simply omitted the key normalizes to it. One spelling of "not stated"
 * downstream, because two would mean every consumer has to remember both.
 */
const optional = <T extends z.ZodTypeAny>(inner: T) =>
  inner.nullable().default(null);

export const intakeSchema = z
  .object({
    /** 1. Transaction type. */
    loanPurpose: optional(enumOf(LOAN_PURPOSE_CODES)),
    /** 2. Property value (appraised or purchase price). */
    propertyValue: optional(z.number().positive().max(100_000_000)),
    /**
     * 3. Loan amount. The form lets the broker enter either this or LTV; one is derived.
     *
     * LTV is NOT a field here, and its absence is the design rather than an omission.
     * Henry, 2026-09-01: "more often than not the broker knows the ltv faster than the
     * loan amount", so the form now offers both boxes and writes each into the other -
     * but only one of them can be the stored figure. Storing both would mean a scenario
     * that can disagree with itself, and everything downstream reads the pair: the
     * refine below compares the loan against the property value, toScenario derives ltv
     * from the two amounts, and the near-miss logic answers "what loan amount would fit"
     * off loanAmount. A stored LTV would give all of those a second, unreconciled
     * opinion of the same file. So LTV stays a calculator at the form boundary, and what
     * leaves the form is the loan amount it produced.
     */
    loanAmount: optional(z.number().positive().max(100_000_000)),
    /**
     * 4. Cash in hand - the money the borrower actually walks away with.
     *
     * Henry, 2026-09-01: "we need to add cash in hand as a field", "before occupancy".
     * It is a real grid dimension rather than a nicety: two of the four lenders in the
     * corpus cap it directly, and both cap it as a function of LTV. Arc's Clean Slate
     * DSCR prints "> 65% LTV: Max $500,000 cash-in-hand; <= 65% LTV: Unlimited", and
     * Logan prints a $500,000 cap above 70% LTV at DSCR >1.00 and above 60% at
     * DSCR <1.00 - strictly greater and strictly less, which leaves a file at exactly
     * 1.00 in neither printed band; see dscrFrom below for how that gap is closed. A
     * file that clears every LTV cell and asks for $700,000 back is declined by both,
     * and until this field existed neither decline was visible.
     *
     * ASKED ONLY ON A CASH-OUT, and nulled on submit when the form is not showing it.
     * Loan purpose is question 1, so by the time a broker reaches this box the answer is
     * already known - a purchase has no proceeds to take away and a rate/term refinance
     * is defined by not taking any, so a box sitting greyed on every search would be
     * eleven-twelfths noise. See `cashOut` in ScenarioDesk.
     *
     * Zero is a real answer and NOT a blank. A cash-out that pays off a lien and hands
     * the borrower nothing is a file that no cash-in-hand cap can bind, which is a
     * different and much better answer than "we could not test it".
     *
     * It stays out of WIDENABLE_FIELDS on purpose. A blank here is not a wider question
     * the way a blank occupancy is - there is no set of values to sweep, there is a
     * published cap we cannot hold anything up against. That case comes back as the
     * `unknown` outcome from evaluateCashInHandLimit below, which is a sentence to put
     * in front of the AE rather than an axis to widen.
     */
    cashInHand: optional(z.number().min(0).max(100_000_000)),
    /** 5. Occupancy. */
    occupancy: optional(enumOf(OCCUPANCY_CODES)),
    /** 6. Property type. */
    propertyType: optional(enumOf(PROPERTY_TYPE_CODES)),
    /**
     * 6b. How many units, asked only for the one property type whose label does not say.
     *
     * Every other option carries its count in its name, so this is null on all of them
     * and the form does not render the box. `units_5_8` is open-ended on purpose - the
     * six programs that go past four units stop at 8, 9 and 10 - and until now the
     * evaluator guessed 5 for it, the low end being the end that never excludes a lender
     * wrongly. The guess stays for a broker who does not answer; see
     * `unitsForPropertyType`, which takes this number and prefers it when it is there.
     *
     * The floor is 5 rather than 1 because a smaller building is not a missing answer,
     * it is a different property type with its own option two lines up the dropdown, and
     * a 3 typed here would quietly re-scope a search the broker has already scoped. That
     * is a contradiction to say out loud rather than a value to accept.
     *
     * There is no lending ceiling in the max, because the ceiling is the lender's: 500
     * is a typo guard. A genuine 40-unit building matches no residential program and
     * should come back saying so, which is a truthful answer and a different one from
     * a rejected form.
     */
    unitCount: optional(z.number().int().min(5).max(500)),
    /** 7. State. */
    state: optional(z.enum(US_STATES)),
    /** 8. FICO. 0 = no credit score, which is NOT the same as leaving it blank. */
    ficoScore: optional(ficoSchema),
    /** 9. Loan program == documentation type; the grid column. */
    docType: optional(enumOf(DOC_TYPE_CODES)),
    /** 10. DTI %, for income-documented programs. */
    dti: z.number().min(0).max(100).nullable().default(null),
    /** 10b. DSCR ratio, for cash-flow programs. Mutually exclusive with dti in practice. */
    dscr: z.number().min(0).max(10).nullable().default(null),
    /** 11. Citizenship / borrower status. */
    citizenship: optional(enumOf(CITIZENSHIP_CODES)),
    /**
     * 12. Recent credit event and its seasoning in months.
     *
     * Left with its 'none' default rather than made nullable like its neighbours. The
     * control is a select with no placeholder, so "None" is a real answer the broker can
     * read off the screen rather than an untouched box - there is no blank state to
     * widen from, and treating one as unknown would put a credit-event caveat on every
     * clean file in the product.
     */
    creditEvent: enumOf(CREDIT_EVENT_CODES).default('none'),
    creditEventSeasoningMonths: z.number().int().min(0).max(600).nullable().default(null),

    /** Reserves in months of PITIA — a grid dimension at Vista and Deephaven. */
    reservesMonths: z.number().int().min(0).max(120).nullable().default(null),

    /**
     * The free-text box. This is what drives the rules layer and guideline retrieval —
     * and, since Henry's 2026-08-27 note, whether the guideline layer runs at all. See
     * `guidelinesNeeded` in the engine.
     */
    funkyText: z.string().max(2000).default(''),
  })
  .refine((v) => v.loanAmount === null || v.propertyValue === null
    || v.loanAmount <= v.propertyValue, {
    // Guarded on both being present, because with either one blank there is no
    // relationship to violate - and a refine that fired on a null would report a typo
    // to a broker who has simply not filled the box in yet.
    message: 'Loan amount cannot exceed property value',
    path: ['loanAmount'],
  })
  .refine((v) => v.cashInHand === null || v.loanAmount === null
    || v.cashInHand <= v.loanAmount, {
    // The proceeds come OUT of the loan - after the existing lien and the closing costs
    // are paid, so in practice strictly out of it - and a figure larger than the loan is
    // the same stray zero the refine above catches one field up. Guarded on both being
    // present for the same reason: with the loan amount blank there is no relationship to
    // violate, and a broker who has filled in one box of two is not making an error yet.
    message: 'Cash in hand cannot exceed the loan amount',
    path: ['cashInHand'],
  });

export type Intake = z.infer<typeof intakeSchema>;

/** LTV as a percentage, rounded to 2dp. Always derived — never stored on the form. */
export const computeLtv = (propertyValue: number, loanAmount: number): number =>
  Math.round((loanAmount / propertyValue) * 10_000) / 100;

/** Inverse: the loan amount that would hit a target LTV. Used for near-miss "what would fix it". */
export const loanAmountAtLtv = (propertyValue: number, ltv: number): number =>
  Math.round((propertyValue * ltv) / 100);

/**
 * The two fields that cannot be widened away, and the reason this function exists.
 *
 * Every other blank field makes the search broader: no doc type means every column of
 * the grid, no FICO means every rung of the ladder. Property value and loan amount are
 * different in kind, because the question the grid answers is "does this LTV fit under
 * the published maximum" and without both numbers there is no LTV to ask about. There
 * is nothing to widen to - a sweep with no LTV returns a row for every lender and can
 * say nothing about any of them.
 *
 * So this case is named and refused in words rather than left to fall through into an
 * empty result set, which is the shape a broker reads as "nobody can do this file".
 */
export function missingLtvInputs(
  intake: Pick<Intake, 'propertyValue' | 'loanAmount'>,
): ('propertyValue' | 'loanAmount')[] {
  const out: ('propertyValue' | 'loanAmount')[] = [];
  if (intake.propertyValue === null) out.push('propertyValue');
  if (intake.loanAmount === null) out.push('loanAmount');
  return out;
}

/** The sentence a person should read when the LTV inputs are not there. */
export function ltvUnavailableMessage(missing: readonly string[]): string {
  const names = missing.map((m) => LTV_INPUT_LABELS[m] ?? m);
  return (
    `Every lender's grid is a maximum LTV, so this search needs both the property value `
    + `and the loan amount to have a question to ask. ${names.join(' and ')} `
    + `${names.length === 1 ? 'is' : 'are'} blank, so there is no LTV to check and no `
    + `answer to give - not "no lender can do this". Fill ${names.length === 1 ? 'it' : 'them'} `
    + `in and every other field can stay blank.`
  );
}

const LTV_INPUT_LABELS: Record<string, string> = {
  propertyValue: 'Property value',
  loanAmount: 'Loan amount',
};

/**
 * The fields a blank value widens the search on, in the order the form asks them.
 *
 * Kept as data rather than as seven `if` statements because three layers need the same
 * list: the evaluator decides what to widen, the synthesis context tells the model what
 * it is looking at, and the answer states what it assumed. Those three drifting apart is
 * how a widened field starts reading as a confirmed one.
 */
export const WIDENABLE_FIELDS = [
  'loanPurpose', 'occupancy', 'propertyType', 'state', 'ficoScore', 'docType',
  'citizenship',
] as const;
export type WidenableField = (typeof WIDENABLE_FIELDS)[number];

/** How to name a widened field to a broker. */
export const WIDENABLE_FIELD_LABELS: Record<WidenableField, string> = {
  loanPurpose: 'transaction type',
  occupancy: 'occupancy',
  propertyType: 'property type',
  state: 'property state',
  ficoScore: 'credit score',
  docType: 'documentation type',
  citizenship: 'borrower status',
};

/** Which of those the broker left blank. Empty on a fully answered form. */
export function widenedFields(intake: Intake): WidenableField[] {
  return WIDENABLE_FIELDS.filter((f) => intake[f] === null);
}

/**
 * The normalized scenario the rest of the pipeline consumes. Distinct from `Intake`
 * because the rules layer rewrites fields on it (a `derive_fact` rule can replace
 * `ficoScore` with a co-guarantor's score) and we must keep the broker's original
 * input intact for audit and replay.
 *
 * The four derived fields are nullable for the same reason the intake ones are: a
 * scenario is allowed to be partial, and `ficoScore: 0` is a borrower with no score
 * while `ficoScore: null` is a form nobody filled in. Collapsing those two would quote
 * every unanswered form off a lender's no-score grid.
 */
export interface Scenario {
  readonly intake: Intake;
  /** Derived, post-rules. Starts as a copy of the corresponding intake fields. */
  ficoScore: number | null;
  docType: DocTypeCode | null;
  loanPurpose: LoanPurpose | null;
  /** Null when either amount is missing; see missingLtvInputs. */
  ltv: number | null;
  /** Free-form facts derived by rules, e.g. decisionFicoBasis. Surfaced in the answer. */
  derived: Record<string, unknown>;
  /** Rules that fired, in priority order. */
  firedRuleIds: string[];
  /** Questions the broker must answer for a derived fact to hold. */
  openQuestions: string[];
}

export function toScenario(intake: Intake): Scenario {
  return {
    intake,
    ficoScore: intake.ficoScore,
    docType: intake.docType,
    loanPurpose: intake.loanPurpose,
    ltv: intake.propertyValue !== null && intake.loanAmount !== null
      ? computeLtv(intake.propertyValue, intake.loanAmount)
      : null,
    derived: {},
    firedRuleIds: [],
    openQuestions: [],
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * CASH-IN-HAND CAPS, AND THE ONE PLACE THIS PRODUCT INFERS A RULE NOBODY PRINTED
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * How a lender's cash-in-hand cap is shaped, and why it is not just a number.
 *
 * `cashout.max_cash_in_hand` is a money attribute in the registry, which suggests one
 * figure per program. It is not one figure at either of the two lenders that publish it,
 * because both scope the cap by LTV and one of them scopes it by DSCR as well:
 *
 *   Arc, Clean Slate DSCR:
 *     "> 65% LTV: Max $500,000 cash-in-hand; <= 65% LTV: Unlimited cash-in-hand"
 *   Logan (verbatim from the page - note the strict operators):
 *     "Cash in hand limited to $500,000 for LTV's greater than 70% for DSCR >1.00"
 *     "Cash in hand limited to $500,000 for LTV's greater than 60% for DSCR<1.00"
 *
 * Stored as a bare $500,000 those become "this program caps cash at half a million",
 * which is wrong for every borrower under the threshold - the majority of them - and
 * wrong in the direction that costs a lender a deal they would have done. Logan is the
 * shape that forces the issue: the SAME cap sits at two different LTVs depending on the
 * coverage ratio, so any shape without a DSCR scope is wrong for Logan on day one and
 * cannot be patched into rightness later without re-reading the matrix.
 *
 * So a limit is a BAND, and a program carries a list of them. Logan is two entries that
 * tile the DSCR axis; Arc is one entry with no DSCR scope at all. The list is folded by
 * evaluateCashInHandLimits, which takes the strictest applicable band - the same
 * direction (`lower_is_stricter`) the attribute registry already declares for this key.
 */
export interface CashInHandLimit {
  /** The cap in dollars, for a file that lands inside this band. */
  readonly capUsd: number;
  /**
   * The LTV the cap sits above. Below it, this band does not apply.
   *
   * A cap with no LTV condition at all is `aboveLtv: 0, inclusiveOfThreshold: true`,
   * which reaches every file and leaves the complement unreachable - there is no "below"
   * for the inference to fill in. That is how the flat caps get encoded, and there is at
   * least one in the corpus already: TransAm's investor overlay puts a $300,000 ceiling
   * on a JLD program whose base guideline prints "Unlimited". The fold below is what
   * makes that overlay win, and it wins for the general reason rather than a special
   * case - a band that binds outranks a band that exempts, whichever document it came
   * from, which is `lower_is_stricter` doing what the registry says it does.
   */
  readonly aboveLtv: number;
  /**
   * Whether a file AT the threshold is capped.
   *
   * Both lenders in the corpus print ">" and Arc prints the complement as "<= 65%", so
   * the threshold LTV itself is uncapped and this is false for both. It is a field
   * rather than an assumption because ">= 75%" is an equally normal thing for a matrix
   * to print, and the file that lands on exactly 65.00% or 70.00% is not rare - it is
   * the file a broker deliberately structured to sit on the line.
   */
  readonly inclusiveOfThreshold: boolean;
  /**
   * ═══ THE CEILING ON THE BAND: A CAP PRINTED "AT OR BELOW" AN LTV ═══
   *
   * The LTV this band stops at, or null when it runs to the top of the axis - which is
   * every band written before 2026-10-10 and still most of them.
   *
   * Lenders print the lower rung of a ladder as a cap of its own, not as "unlimited":
   *
   *   MAXEX DSCR 2:         "-LTV <=60%: $1,000,000"   "-LTV >60%: $500,000"
   *   Vanquish DSCR p4:     "Maximum Cash Out | LTV ≤ 65% $1,000,000"
   *   Silver Hill:          "LTV ≤ 50%: $1,000,000"  "LTV > 50 & < 70: $500,000"
   *                         "LTV ≥ 70%: $250,000"
   *
   * Until this field existed the lower rung had nowhere to put its ceiling, so ingest
   * wrote it as a FLAT cap (`aboveLtv: 0`) and relied on the fold - "the strictest
   * applicable band wins" - to let the upper rung out-strict it above the line. That
   * reasoning holds only where the upper rung is the stricter one, and it never got
   * the chance to run: the reader (`cashInHandLimitFromRow`) refuses a flat row whose
   * quote names a threshold, which it is right to do, so the $1,000,000 rung became an
   * untested cap; the engine then deduplicated it away against the $500,000 band built
   * off a sibling sentence, and the $500,000 band's inferred "unlimited below 60%"
   * cleared the file. Inquiry 8890283d - $1,700,000 cash at 50% LTV - passed MAXEX
   * DSCR 2 and CapitalEdge Vanquish DSCR on that path, two lenders who print a
   * $1,000,000 ceiling at exactly that LTV. Found 2026-10-10.
   *
   * So the rung says where it stops. A file ABOVE the ceiling is outside this band, the
   * same way a file below `aboveLtv` is, and the sibling rung that covers it answers.
   * What it must never do is clear the file: see `evaluateCashInHandLimit`, which returns
   * an above-the-ceiling exemption with NO band attached, so it cannot be quoted as the
   * cap the file "cleared" and cannot settle the cash question for the gate.
   *
   * Optional on the type because verdict JSON stored before this field existed carries
   * neither key, and every reader tests it with `!= null`. The schema always fills both.
   */
  readonly atOrBelowLtv?: number | null;
  /**
   * Whether a file AT the ceiling is inside the band. True for "<=" and "≤", false for
   * "<" - NMSI prints "LTV < 65%: $1,000,000" beside "LTV ≥ 65%: $500,000", and the two
   * tile the axis only if 65.00% belongs to the upper rung. Absent reads as true.
   */
  readonly inclusiveOfUpper?: boolean;
  /**
   * The DSCR window this band describes: [dscrFrom, dscrBelow), either end null for
   * open. Both null means the band is not DSCR-scoped at all (Arc).
   *
   * Half-open, and it closes a gap the lender left rather than reproducing one.
   *
   * This comment used to say the shape was Logan's own wording - that they print
   * "DSCR >= 1.00" and "DSCR < 1.00", so 1.00 falls in the first and only the first.
   * That was wrong, and it was wrong in the brief before it was wrong here. Logan's page
   * reads "for DSCR >1.00" and "DSCR<1.00", STRICTLY, verified against doc_page.page_text.
   * A file at exactly 1.00 is therefore in NEITHER printed band.
   *
   * Left alone that is not a rounding error, it is a silent yes: a 75% LTV cash-out at
   * DSCR 1.00 would match no band and be told no cap applies. So the gap is closed
   * UPWARD, into the band whose threshold is 70% - dscrFrom 1.00, dscrBelow null. Closing
   * it downward would hand that borrower the stricter 60% threshold, which is the failure
   * this whole shape exists to avoid: being wrong in the direction that costs a lender a
   * deal they would have done. `printedAs` keeps the verbatim ">1.00" so the inference is
   * visible to anyone reading the row back.
   */
  readonly dscrFrom: number | null;
  readonly dscrBelow: number | null;
  /**
   * ═══ THE SELECTOR AXES: WHICH TIER OF A TIERED CAP THIS BAND IS ═══
   *
   * The LTV threshold above and the DSCR window above it say WHEN the cap is in force.
   * These six say WHICH OF SEVERAL CAPS is the one in force, for a lender who prints a
   * ladder rather than a number. Acra, verbatim off its matrix:
   *
   *   "When LTV > 65% Max Cash-in-Hand: $1,000,000 for loans >= $1,500,000,
   *    $500,000 for loans < $1,500,000"
   *
   * That is two bands at one LTV threshold, differing only in which loans they select,
   * and until 2026-09-02 the shape had nowhere to write the selector - so ingest read
   * the sentence, refused it BY NAME (`scoped_by_loan_amount`) and filed it as prose.
   * Seven Acra rows, plus A&D's two, went to the AE as a question this product already
   * held every fact to answer. Henry, on exactly this: "fico we have, unit count we
   * either already added or it was requested by me to add when I said have broker select
   * 5+ then type in unit."
   *
   * All three are on the intake. The loan amount is REQUIRED - there is no LTV without
   * it - the score is question 3, and the unit count is the box the "5+ Units" property
   * type opens (`unitsForPropertyType`). What was missing was this, the room to write the
   * bound down, which is why lifting the refusal had to start here rather than in ingest.
   *
   * ── Half-open on money, inclusive on counts, and the difference is not cosmetic ──
   *
   * `loanAmountFrom`/`loanAmountBelow` and `ficoFrom`/`ficoBelow` are [from, below), the
   * same half-open window as the DSCR pair and for the same reason: Acra's ">= $1.5MM"
   * and "< $1.5MM" tile the axis exactly, with the boundary loan landing in one tier and
   * one only. A closed-closed pair would put a $1,500,000 loan in both, and the fold
   * would then quote it the stricter $500,000 the matrix does not put it under.
   *
   * `unitsFrom`/`unitsThrough` are INCLUSIVE at both ends, because a matrix prints
   * "5-8 unit: $1,000,000" and means 8. Writing that half-open would need a 9 the lender
   * never printed, and this file does not invent numbers to make a shape uniform.
   *
   * ── What this does NOT do: widen the complement inference ──
   *
   * A file outside a band's selector range is exempt FROM THAT BAND, exactly as a file
   * outside its DSCR window is - the band describes a different loan and says nothing
   * about this one. That is not the carve-out below being extended to a third axis. It
   * is safe only because ingest refuses to store a tiered cap whose tiers do not cover
   * the axis between them: see `incomplete_tiering` in packages/ingest/src/cash-in-hand.ts.
   * Where the tiers tile, some sibling band always answers; where they do not, no band is
   * stored at all and the sentence goes to the AE as an untested cap. Neither route ever
   * turns a range the lender capped into a range nobody checked.
   *
   * Null on every axis is the ordinary case and means the band is not tiered at all.
   */
  readonly loanAmountFrom: number | null;
  readonly loanAmountBelow: number | null;
  readonly ficoFrom: number | null;
  readonly ficoBelow: number | null;
  readonly unitsFrom: number | null;
  readonly unitsThrough: number | null;
  /**
   * ═══ THE OCCUPANCY AXIS, AND WHY IT IS A SET RATHER THAN A RANGE ═══
   *
   * The occupancies this band selects. Null means the band is not occupancy-scoped and
   * reaches every file, which is every band written before 2026-09-02 and most of them
   * since - only one lender in the corpus scopes a cash-in-hand cap this way.
   *
   * That lender is CapitalEdge, whose Vanquish channel prints two ladders where the
   * other five axes see one. Verbatim off page 1 of the Vanquish overlay sheet, repeated
   * word for word at §2.9.1.3 of the underwriting guide:
   *
   *   Owner Occupied:     LTVs <= 65% - Unlimited;     LTVs > 65% - $500,000
   *   Non-Owner Occupied: LTVs <= 65% - $1,000,000;    LTVs > 65% - $500,000
   *
   * With no axis to write the qualifier on, those two ladders collapsed onto one. The
   * "Non-Owner Occupied: <= 65% - $1,000,000" rung became a flat band reaching every
   * file, the fold took the strictest applicable cap, and an OWNER-OCCUPIED cash-out at
   * 50% LTV was quoted a $1,000,000 ceiling where CapitalEdge prints "Unlimited". That
   * is the conservative direction and it is still wrong: it costs a borrower a deal the
   * lender would have done, which is the failure the whole band shape exists to end.
   *
   * ── A SET, not a from/through pair ───────────────────────────────────────────────
   *
   * The other five selectors are windows on a number because loan amounts, scores and
   * unit counts are ordered and unbounded. Occupancy is three named codes with no order
   * between them: `primary`, `second_home`, `investment`. There is no "above" a second
   * home. So the band names the codes it selects, and the evaluator asks for membership
   * rather than for a range.
   *
   * Non-empty when non-null. A band scoped to nothing selects nothing and would be a
   * band that can never match - invisible rather than wrong, which is the same failure
   * `cashInHandLimitSchema` refuses an inverted DSCR window for.
   *
   * ── "Non-Owner Occupied" is two of the three codes, and that is a reading ─────────
   *
   * CapitalEdge prints a binary. This product's vocabulary has three codes, because
   * every lender's GRID has three: the Vanquish program itself publishes 60 primary, 60
   * second-home and 60 investment cells. So "Non-Owner Occupied" has to be read as the
   * complement of owner-occupied - `second_home` and `investment` together - and that
   * reading is made in ingest, where the page is, rather than here. It is the standard
   * trade meaning (owner-occupied is the primary residence; a second home is not one)
   * and the same guide uses the phrase that way elsewhere: "commercial or non-owner
   * occupied residential real estate" at §5.6.2.2.
   *
   * ── What this deliberately does NOT do ───────────────────────────────────────────
   *
   * It does not widen the complement inference below. That carve-out is "a cap stated
   * above an LTV means unlimited below it", on the LTV axis, under one key, and it stays
   * that. An occupancy this band does not name is NOT inferred to be unlimited - it is a
   * range of the axis the statement may simply not have addressed, and ingest refuses
   * the whole statement rather than let one ladder answer for an occupancy it says
   * nothing about. See `incomplete_occupancy_scope` in packages/ingest/src/cash-in-hand.ts.
   */
  readonly occupancies: readonly Occupancy[] | null;
  /**
   * Whether the lender PRINTED the "unlimited below the threshold" half, or whether we
   * inferred it. See the inference note below - this field is the whole audit trail for
   * that inference, and it has no default on purpose.
   */
  readonly unlimitedBelow: ComplementProvenance;
  /**
   * ═══ THE PRINTED WAY OUT OF THE CAP ═══
   *
   * Two lenders write the same construct: a cap, and one documented condition that
   * removes it. CapitalEdge - "Max cash-in-hand: <=70% LTV unlimited; >70% LTV
   * $1,000,000 (or unlimited with 18 months' reserves exclusive of cash back)" - and NQM,
   * twice, on Flex Supreme and Investor DSCR.
   *
   * Until 2026-09-04 this shape had no home, so ingest refused the whole statement as
   * `conditional_escape` and the cap went to the AE. That refusal is honest and it is
   * also expensive in a way that took a client complaint to see: the escape hangs off ONE
   * rung of a ladder, and refusing the statement threw away the OTHER rung with it. The
   * rung it threw away was "<=70% LTV unlimited", which is the half that answers a
   * 66.67% LTV file, and CapitalEdge only survived that because it happens to print the
   * same rule again on the next page in a simpler form. NQM prints it once.
   *
   * So the condition gets a shape. It is deliberately ONE shape and not a general
   * grammar: reserves, in months, which is question 14 on the form and therefore a fact
   * this product can actually test. A condition we cannot test is still a refusal, and
   * still goes to the AE - `conditional_escape` did not go away, it stopped being the
   * answer to a sentence we can read.
   *
   * Null on every band before this shape existed, and null is right for all of them: a
   * band with no escape is a cap with no way out, which is what they are.
   */
  readonly escape: CashInHandEscape | null;
  /** The sentence as printed, verbatim. Every claim in this product is citable. */
  readonly printedAs: string;
}

/**
 * A condition the lender printed that lifts the cap.
 *
 * `kind` is a single-member union rather than a bare shape, because the next condition
 * this corpus produces will not be reserves and the evaluator has to fail to compile
 * rather than silently treat it as one. `months` is what the borrower must hold;
 * `printedAs` is the lender's own words for it, so a pass resting on this condition can
 * quote the sentence that granted it.
 */
export interface CashInHandEscape {
  readonly kind: 'reserves_months';
  readonly months: number;
  readonly printedAs: string;
}

export type ComplementProvenance = 'printed' | 'inferred';

/**
 * ═══ THE INFERENCE, AND EXACTLY HOW FAR IT GOES ═══
 *
 * Henry, 2026-09-01: "If a lender sets a cash in hand limitation for greater than or
 * equal to a certain LTV, assume it's unlimited for below that."
 *
 * THIS CONTRADICTS THE STANDING RULE OF THIS PRODUCT, and the contradiction is
 * deliberate and the client's to make. Everywhere else, a guideline that does not
 * address a question is named and sent to the AE - silence is never converted into a
 * yes, because a lender who has not said "allowed" has not said it and a broker quoting
 * us on the difference gets burned. That principle is intact. This is one carve-out in
 * it, backed by the client's own experience of how these matrices are written: Arc
 * printed both halves ("<= 65% LTV: Unlimited") and Logan simply did not bother, because
 * in the trade a cap announced above a line is understood to mean no cap below it.
 *
 * WHAT THE CARVE-OUT COVERS - all four, together:
 *   1. the attribute `cashout.max_cash_in_hand`, and no other key;
 *   2. a cap stated as applying ABOVE an LTV threshold;
 *   3. inferring the complement UNLIMITED, i.e. only ever loosening below the line;
 *   4. within the band's own DSCR scope, if it has one.
 *
 * WHAT IT IS NOT. It is not a licence to infer the complement of any one-sided rule.
 * "Max 80% LTV for a 700 FICO" does not mean unlimited LTV at 720. "Reserves of 6 months
 * above $1.5M" does not mean no reserves below it. "Interest-only available above 700"
 * says nothing whatsoever about 699. Every one of those is a silence, and a silence is
 * still a question for the AE. If a future rule looks like it wants this treatment, the
 * answer is to ask Henry about that rule specifically and get the same kind of
 * experience-backed answer - not to widen this one, and not to add a second key to
 * `mayInferUnlimitedComplement`.
 *
 * The inference is not invisible where it is used, either: a finding that rests on it
 * carries `restsOnInference`, and `cashInHandInferenceNote` is the sentence the answer
 * puts in front of the broker. An inferred yes that reads exactly like a printed yes is
 * the failure mode this whole design is arranged against.
 */
export const INFERABLE_COMPLEMENT_KEY = 'cashout.max_cash_in_hand' as const;

/**
 * The gate. Ingest asks this before completing a one-sided cap into a band, and the
 * answer is true for exactly one attribute key in the registry.
 *
 * A function rather than a comment because a comment cannot be tested, and the test that
 * matters here is the negative one: `loan.max_amount`, `reserves.min_months`, `dti.max`
 * and `credit.min_fico` all publish one-sided thresholds too, and all four must come
 * back false. That test is the thing standing between this carve-out and the general
 * rule it must never become.
 */
export function mayInferUnlimitedComplement(attributeKey: string): boolean {
  return attributeKey === INFERABLE_COMPLEMENT_KEY;
}

/**
 * The file, as far as a cash-in-hand cap is concerned.
 *
 * The last three are the SELECTOR facts, and they are here because they were never
 * missing from the file - only from this type. A band tiered by loan amount handed a
 * request that could not answer "how big is the loan" would come back `unknown` for want
 * of a number the intake requires, which is a worse answer than the one it replaced: a
 * hedge in place of an authored near miss. So they arrive together with the bounds.
 *
 * `units` is the COUNT, already resolved through `unitsForPropertyType` by whoever built
 * the request - null on mixed use and commercial, where the question does not apply.
 * Resolving a property type here would be a second opinion about how many units the
 * building has, held one layer away from the sweep that filtered on the first.
 */
export interface CashInHandRequest {
  readonly loanPurpose: LoanPurpose | null;
  readonly ltv: number | null;
  readonly dscr: number | null;
  readonly cashInHand: number | null;
  readonly loanAmount: number | null;
  readonly ficoScore: number | null;
  readonly units: number | null;
  /**
   * Months of PITIA reserves, question 14 on the form. Read ONLY by a band carrying an
   * `escape`, and asked for only where the cap would otherwise bind - a borrower whose
   * cash is inside the cap is never asked to prove reserves he does not need.
   */
  readonly reservesMonths: number | null;
  /**
   * Question 5 on the form, and the one selector fact that is also a grid dimension in
   * its own right - a verdict exists for this channel because a cell matched on it, or
   * because the broker left it blank and the sweep widened. Null is therefore rare here
   * in a way it is not for the score, and when it happens it is the same widening the
   * rest of the answer already discloses.
   */
  readonly occupancy: Occupancy | null;
}

/**
 * `binds`          - a cap applies and the borrower is asking for more than it.
 * `within_cap`     - a cap applies and the request fits under it.
 * `does_not_apply` - no cap is in force on this file: wrong purpose, no cash taken,
 *                    under the LTV threshold, above a bounded rung's ceiling (with
 *                    `limit` null - see `atOrBelowLtv`), or outside the band's DSCR
 *                    window.
 * `unknown`        - the band might apply and the file does not say enough to tell.
 *                    NOT a pass. This is the silence-means-ask-the-AE path, kept whole:
 *                    the broker who leaves the cash box blank on a 75% LTV cash-out gets
 *                    the cap quoted at them and a question, not a green tick.
 */
export type CashInHandOutcome = 'binds' | 'within_cap' | 'does_not_apply' | 'unknown';

/**
 * Which answer was missing, so the caller can ask for that one rather than "more info".
 *
 * The last four are here for completeness rather than for the corpus: every one of them
 * is a field the intake collects, and `loanAmount` cannot be absent at all on a file that
 * reached the sweep, because there is no LTV without it. They exist because a selector
 * bound with no value to test is the one thing the evaluator must not read as a pass, and
 * "cannot happen" is not a reason to leave the branch unwritten - a broker on a mixed-use
 * property has no unit count, and a cap tiered by units then has nothing to select with.
 *
 * `occupancy` is the one of the four a broker can realistically leave blank AND that then
 * changes an answer: it is an optional dropdown, a blank WIDENS the grid sweep rather
 * than emptying it, so a widened sweep can reach a channel whose cap is printed per
 * occupancy. That file gets a question naming the dropdown, not a pass.
 */
export type CashInHandUnknownInput =
  | 'loanPurpose' | 'ltv' | 'dscr' | 'cashInHand'
  | 'loanAmount' | 'ficoScore' | 'units' | 'occupancy' | 'reservesMonths';

export interface CashInHandFinding {
  readonly outcome: CashInHandOutcome;
  /** The cap in force, in dollars. Null when none applies or none could be identified. */
  readonly capUsd: number | null;
  /** cap minus request, when both are known. Negative when it binds - the near-miss gap. */
  readonly headroomUsd: number | null;
  /**
   * True only when this answer RESTS on the inferred complement - that is, when the file
   * came out clean because it sits below a threshold whose lower half the lender never
   * printed. False on a printed complement, and false on `binds`/`within_cap`, where it
   * is the printed half of the rule doing the work.
   */
  readonly restsOnInference: boolean;
  readonly missing: readonly CashInHandUnknownInput[];
  /** The band that produced this finding, for citation. Null when no band applies. */
  readonly limit: CashInHandLimit | null;
  /**
   * True only when this file cleared a cap that WOULD have bound, on the strength of the
   * lender's printed escape condition.
   *
   * Distinct from every other clean outcome and it has to be, because the sentence the
   * answer owes a broker is different. An ordinary exemption says "the cap does not reach
   * you". This says "the cap reaches you and you are out of it BECAUSE you hold the
   * reserves" - which is a condition to paper, not a fact to forget. Rendered as its own
   * line in the synthesis context for exactly that reason.
   */
  readonly escaped: boolean;
}

const NOTHING_APPLIES: CashInHandFinding = {
  outcome: 'does_not_apply', capUsd: null, headroomUsd: null,
  restsOnInference: false, missing: [], limit: null, escaped: false,
};

/**
 * Does this band bind on this file?
 *
 * Pure, total, and ordered so that the cheapest DEFINITIVE exemptions are taken first -
 * which is not a performance concern but an accuracy one. A file below the LTV threshold
 * is exempt whatever its DSCR and whatever the broker did or did not type in the cash
 * box, so testing the threshold before the DSCR window means a 55% LTV cash-out with no
 * DSCR stated comes back clean instead of coming back as a question nobody needed to
 * ask. Every `unknown` this function can avoid emitting is a caveat that does not go in
 * front of a broker, and a product that hedges on files it could have answered is read
 * as one that hedges on everything.
 */
export function evaluateCashInHandLimit(
  limit: CashInHandLimit,
  req: CashInHandRequest,
): CashInHandFinding {
  const exempt = (restsOnInference = false): CashInHandFinding => ({
    ...NOTHING_APPLIES, restsOnInference, limit,
  });

  // A purchase has no proceeds and a rate/term refinance is defined by taking none, so
  // the cap has nothing to bite on. A null purpose is NOT this case - see below.
  if (req.loanPurpose !== null && !isCashOut(req.loanPurpose)) return exempt();

  // Zero cash asked for. A cash-out that clears a lien and hands the borrower nothing
  // passes every cap there is, and it passes them without needing the LTV, the DSCR or
  // the purpose to be known.
  if (req.cashInHand === 0) return exempt();

  if (req.ltv === null) {
    return { ...NOTHING_APPLIES, outcome: 'unknown', missing: ['ltv'], limit };
  }

  const overThreshold = limit.inclusiveOfThreshold
    ? req.ltv >= limit.aboveLtv
    : req.ltv > limit.aboveLtv;
  // ═══ The inference is applied HERE and nowhere else ═══
  // Below the threshold the band does not apply, whether or not the lender printed the
  // sentence saying so. `restsOnInference` records which of those two it was, and it is
  // the only thing separating "Arc says unlimited" from "Logan did not say".
  if (!overThreshold) return exempt(limit.unlimitedBelow === 'inferred');

  // ═══ Above the band's ceiling: outside it, and NOT a clear ═══
  //
  // A rung printed "at or below 60%" says nothing about a 61% file - the rung above it
  // does. So the file is outside this band, exactly as one below `aboveLtv` is, and the
  // finding carries NO band. That null is the safety, not a convenience: a finding with
  // a band attached and `does_not_apply` is what `cashInHandResolved` in the engine
  // reads as "a cap we read, and this file is on the free side of it", and a ceiling the
  // file is above is not a cap this file cleared. If the ceiling were allowed to count,
  // a program whose only rung is bounded would settle every file above it as uncapped -
  // the silent yes the bounded rung was added to stop, arriving from the other end.
  //
  // `!= null` and not `!== null`: verdict JSON stored before 2026-10-10 has neither key,
  // and an absent ceiling is the ordinary unbounded band.
  if (limit.atOrBelowLtv != null) {
    const underCeiling = limit.inclusiveOfUpper !== false
      ? req.ltv <= limit.atOrBelowLtv
      : req.ltv < limit.atOrBelowLtv;
    if (!underCeiling) return NOTHING_APPLIES;
  }

  // The band's own DSCR window, asked only now that the LTV has put the file inside its
  // reach. A band that does not describe this borrower's coverage ratio says nothing
  // about them - Logan's two bands are alternatives, not a pair of tests to pass.
  if (limit.dscrFrom !== null || limit.dscrBelow !== null) {
    if (req.dscr === null) {
      return { ...NOTHING_APPLIES, outcome: 'unknown', missing: ['dscr'], limit };
    }
    const inWindow = (limit.dscrFrom === null || req.dscr >= limit.dscrFrom)
      && (limit.dscrBelow === null || req.dscr < limit.dscrBelow);
    if (!inWindow) return exempt();
  }

  // ═══ The selector axes: is THIS the tier that governs this file? ═══
  //
  // Asked in the same position and with the same meaning as the DSCR window above: a
  // band whose tier does not describe this loan says nothing about it, and the sibling
  // tier that does describe it is in the same list and answers instead. Ingest will not
  // store a tiered cap whose tiers leave a gap between them, so "no tier matched" cannot
  // arise from a cap the lender printed - see `incomplete_tiering` on the parse side.
  //
  // The order runs occupancy, loan amount, score, units, which is the order they are
  // missing in: occupancy is a grid dimension the sweep already matched on, the loan
  // amount is required upstream and cannot be null on a file that got here, the score is
  // a box a broker can leave blank, and the unit count is genuinely absent on commercial
  // and mixed use. Cheapest certainty first, same as everything above.
  const outside = (
    value: number | null, from: number | null, to: number | null,
    inRange: (v: number) => boolean, missingAs: CashInHandUnknownInput,
  ): CashInHandFinding | null => {
    if (from === null && to === null) return null;
    // A bound with nothing to test it against. NOT a pass and not a decline: this band
    // may be the one that governs, and we cannot say. Same treatment a blank cash box
    // gets, and for the same reason.
    if (value === null) {
      return { ...NOTHING_APPLIES, outcome: 'unknown', missing: [missingAs], limit };
    }
    return inRange(value) ? null : exempt();
  };

  // Occupancy is asked FIRST of the four, ahead of the three numeric windows, and the
  // reason is the same "cheapest certainty first" that puts the LTV threshold ahead of
  // the DSCR window: it is the selector least likely to be missing. It is question 5 on
  // the form and a grid dimension at every lender, so a file that reached this channel's
  // verdict usually matched a cell on it. Getting a definitive exemption out of it early
  // means an owner-occupied file never has to be asked for a unit count to be told that
  // the non-owner ladder was never about it.
  //
  // Membership, not a range - see the field comment. An occupancy the band does not name
  // is a band describing a different borrower, exactly as a DSCR window that excludes
  // this file does; it is NOT this file's occupancy being granted unlimited. What makes
  // that safe is that ingest refuses a statement whose occupancy scoping leaves any code
  // of the three unaddressed, so some sibling band always answers.
  if (limit.occupancies !== null) {
    if (req.occupancy === null) {
      return { ...NOTHING_APPLIES, outcome: 'unknown', missing: ['occupancy'], limit };
    }
    if (!limit.occupancies.includes(req.occupancy)) return exempt();
  }

  const tier =
    outside(req.loanAmount, limit.loanAmountFrom, limit.loanAmountBelow,
      (v) => (limit.loanAmountFrom === null || v >= limit.loanAmountFrom)
        && (limit.loanAmountBelow === null || v < limit.loanAmountBelow), 'loanAmount')
    ?? outside(req.ficoScore, limit.ficoFrom, limit.ficoBelow,
      (v) => (limit.ficoFrom === null || v >= limit.ficoFrom)
        && (limit.ficoBelow === null || v < limit.ficoBelow), 'ficoScore')
    // Inclusive at both ends, because "5-8 unit" means 8. See the field comments.
    ?? outside(req.units, limit.unitsFrom, limit.unitsThrough,
      (v) => (limit.unitsFrom === null || v >= limit.unitsFrom)
        && (limit.unitsThrough === null || v <= limit.unitsThrough), 'units');
  if (tier !== null) return tier;

  // The band reaches this file. What is left is what the broker has not told us, and
  // each gap is named so the answer can ask for that one thing. A blank purpose lands
  // here rather than being read as a purchase: at this LTV the cap would bind if this
  // is a cash-out, and "probably not a cash-out" is exactly the kind of quiet yes this
  // product does not give.
  const missing: CashInHandUnknownInput[] = [];
  if (req.loanPurpose === null) missing.push('loanPurpose');
  if (req.cashInHand === null) missing.push('cashInHand');
  if (missing.length > 0) {
    return {
      outcome: 'unknown', capUsd: limit.capUsd, headroomUsd: null,
      restsOnInference: false, missing, limit, escaped: false,
    };
  }

  const headroomUsd = limit.capUsd - (req.cashInHand as number);
  if (headroomUsd >= 0) {
    return {
      outcome: 'within_cap', capUsd: limit.capUsd, headroomUsd,
      restsOnInference: false, missing: [], limit, escaped: false,
    };
  }

  // ═══ The cap binds. Does the lender's own escape let this borrower out? ═══
  //
  // Asked HERE and not earlier, which is the same "cheapest certainty first" ordering the
  // rest of this function runs on and matters for the same reason. A borrower whose cash
  // is inside the cap has no use for the escape, and asking him to state reserves he does
  // not need is a caveat manufactured out of a rule that was never going to bind him.
  //
  // Three outcomes, and the middle one is the point of the whole shape:
  //   reserves stated and sufficient -> the cap is lifted, and the answer must say WHY
  //   reserves not stated            -> unknown, and the form can fix it - not the AE
  //   reserves stated and short      -> binds, exactly as it did before this existed
  //
  // Note what the second one buys. Before this shape, an unreadable escape sent the whole
  // lender to the AE with a question nobody could act on from the form. Now the broker is
  // asked for one number he already has, and the re-run answers it.
  if (limit.escape !== null) {
    if (req.reservesMonths === null) {
      return {
        outcome: 'unknown', capUsd: limit.capUsd, headroomUsd: null,
        restsOnInference: false, missing: ['reservesMonths'], limit, escaped: false,
      };
    }
    if (req.reservesMonths >= limit.escape.months) {
      return { ...NOTHING_APPLIES, limit, escaped: true };
    }
  }

  return {
    outcome: 'binds', capUsd: limit.capUsd, headroomUsd,
    restsOnInference: false, missing: [], limit, escaped: false,
  };
}

/**
 * The same question against every band a program publishes, folded to one answer.
 *
 * The order is what makes it safe. A binding cap is reported ahead of anything else and
 * the strictest of them wins, matching `lower_is_stricter` in the attribute registry. An
 * `unknown` outranks a clean band, because a program is not clear until the band we
 * could not read has been read - taking the pass would be the fold quietly answering the
 * question the file left open. Only with no band binding and none unreadable does a
 * `within_cap` or an exemption come back.
 *
 * On a whole-program exemption the inference flag is ORed across the bands, because any
 * one of them having rested on it is a thing the answer has to disclose. On every other
 * outcome it belongs to the single band that produced the answer.
 */
export function evaluateCashInHandLimits(
  limits: readonly CashInHandLimit[],
  req: CashInHandRequest,
): CashInHandFinding {
  if (limits.length === 0) return NOTHING_APPLIES;
  const findings = limits.map((l) => evaluateCashInHandLimit(l, req));
  const strictestFirst = (a: CashInHandFinding, b: CashInHandFinding): number =>
    (a.capUsd ?? Infinity) - (b.capUsd ?? Infinity);

  const binding = findings.filter((f) => f.outcome === 'binds').sort(strictestFirst);
  if (binding[0]) return binding[0];

  const unknown = findings.filter((f) => f.outcome === 'unknown').sort(strictestFirst);
  const first = unknown[0];
  if (first) {
    // One question per missing answer, not one per band: Logan's two bands both want the
    // cash figure, and asking for it twice reads as a system that is not paying attention.
    const missing = [...new Set(unknown.flatMap((f) => f.missing))];
    return { ...first, missing };
  }

  const within = findings.filter((f) => f.outcome === 'within_cap').sort(strictestFirst);
  if (within[0]) return within[0];

  // An exemption bought by the printed escape is reported AHEAD of an ordinary one, and
  // keeps its band. It is the only clean outcome that carries a condition the broker has
  // to paper, so folding it into a plain `does_not_apply` would drop the one sentence
  // this file owes him - the same class of loss as the fold dropping `limit` did.
  const escaped = findings.find((f) => f.escaped);
  if (escaped) return escaped;

  return {
    ...NOTHING_APPLIES,
    restsOnInference: findings.some((f) => f.restsOnInference),
  };
}

/** The request, read off a scenario, so no caller has to wire the four fields by hand. */
export function cashInHandRequestOf(scenario: Scenario): CashInHandRequest {
  return {
    loanPurpose: scenario.loanPurpose,
    ltv: scenario.ltv,
    dscr: scenario.intake.dscr,
    cashInHand: scenario.intake.cashInHand,
    loanAmount: scenario.intake.loanAmount,
    // The DERIVED score, not the typed one, for the same reason the AE question quotes
    // it: a rule that swapped in a co-guarantor's score is the score the sweep ran with,
    // and a cap tiered at 700 has to be selected by the same number the grid was.
    ficoScore: scenario.ficoScore,
    // The typed answer, and its absence is meaningful: a blank reserves box is what
    // turns a binding cap with a printed escape into a question the FORM can answer,
    // rather than one only the AE can.
    reservesMonths: scenario.intake.reservesMonths,
    units: scenario.intake.propertyType
      ? unitsForPropertyType(scenario.intake.propertyType, scenario.intake.unitCount)
      : null,
    // The typed answer, not a derived one. Nothing in the rule engine rewrites occupancy
    // the way `decisionFicoBasis` rewrites the score, so `Scenario` carries no derived
    // copy of it and inventing one here would be a second opinion about a dropdown.
    occupancy: scenario.intake.occupancy,
  };
}

/**
 * The LTV stretch a band covers, in the words a broker reads it in.
 *
 * Written once, here, because three places say it - the decline's blocker, the model's
 * context line and the inference note - and a cap printed "at or below 60%" that one of
 * them renders as "above 0% LTV" and another as nothing at all is the same rule told
 * three ways. Inclusive-aware at both ends, because the file that sits exactly on the
 * line is the one a broker structured to sit there.
 *
 *   flat (no floor, no ceiling)        ''
 *   floor only                         'above 65% LTV' | 'at or above 65% LTV'
 *   ceiling only                       'at or below 60% LTV' | 'below 65% LTV'
 *   both                               'above 50% and below 70% LTV'
 */
export function cashInHandLtvRange(limit: CashInHandLimit): string {
  const hasFloor = !(limit.aboveLtv === 0 && limit.inclusiveOfThreshold);
  const floor = limit.inclusiveOfThreshold
    ? `at or above ${limit.aboveLtv}%` : `above ${limit.aboveLtv}%`;
  const ceiling = limit.atOrBelowLtv == null ? null
    : limit.inclusiveOfUpper !== false
      ? `at or below ${limit.atOrBelowLtv}%` : `below ${limit.atOrBelowLtv}%`;
  if (ceiling === null) return hasFloor ? `${floor} LTV` : '';
  return hasFloor ? `${floor} and ${ceiling} LTV` : `${ceiling} LTV`;
}

/**
 * What the answer must say when a lender came back clean on the strength of the
 * inference rather than on the strength of what they printed.
 *
 * Null when there is nothing to disclose, so a caller can append it unconditionally.
 * The sentence names the lender's own words, says plainly that the lower half is our
 * reading rather than theirs, and stops short of hedging the answer away - Henry is
 * confident about this one, and a caveat written as a doubt would misrepresent that as
 * much as saying nothing would misrepresent the source.
 */
export function cashInHandInferenceNote(limit: CashInHandLimit): string | null {
  if (limit.unlimitedBelow !== 'inferred') return null;
  const at = `${limit.aboveLtv}%`;
  // A rung bounded on both sides ("LTV > 50 & < 70") is stated for a RANGE, and "only
  // above 50% LTV" would tell the broker the cap runs to the top of the axis, which is
  // the reading this shape was added to stop. The inference itself is unchanged: it is
  // still only ever about the stretch below the floor.
  const where = limit.atOrBelowLtv != null
    ? `only ${cashInHandLtvRange(limit)}`
    : `only above ${at} LTV`;
  return (
    `The matrix states this cap ${where} - "${limit.printedAs}" - and prints `
    + `nothing about below it. Read here as unlimited cash in hand at or under ${at}, `
    + `which is how these caps are written in practice rather than something this lender `
    + `put on the page. Worth confirming with the AE on a file that turns on it.`
  );
}

/**
 * Validation for a band arriving from extraction, where the numbers were read off a PDF.
 *
 * The shape crosses a package boundary and is built by a model, which is the pair of
 * conditions everything else in this package parses rather than trusts. Two things it
 * catches that a type cannot: an LTV threshold outside 0-100, which is a misread of a
 * dollar figure as a percentage, and an inverted DSCR window, which is a band that can
 * never match anything and would therefore be invisible rather than wrong.
 *
 * `unlimitedBelow` has no default here. Extraction has to state whether the lender
 * printed the complement, because that is the difference between a fact and our reading
 * of a silence, and a default would let a missing answer settle as either one.
 */
export const cashInHandLimitSchema = z.object({
  capUsd: z.number().min(0).max(100_000_000),
  aboveLtv: z.number().min(0).max(100),
  inclusiveOfThreshold: z.boolean(),
  // The ceiling defaults to none, for the reason the selector tiers below do: every band
  // stored before 2026-10-10 has no such key and runs to the top of the axis, which is
  // exactly what null means. `inclusiveOfUpper` defaults to true because "<=" is how the
  // corpus prints a lower rung nine times in ten, and on a band with no ceiling it is
  // never read.
  atOrBelowLtv: z.number().min(0).max(100).nullable().default(null),
  inclusiveOfUpper: z.boolean().default(true),
  dscrFrom: z.number().min(0).max(10).nullable().default(null),
  dscrBelow: z.number().min(0).max(10).nullable().default(null),
  // The selector bounds DO default, where `unlimitedBelow` deliberately does not, and
  // the asymmetry is the point. `unlimitedBelow` records what the lender said about a
  // range they may not have addressed, so a default would settle a question nobody
  // answered. These record that the cap is not tiered at all, which is the ordinary case
  // and true of every band stored before 2026-09-02 - the rows read back through
  // `cashInHandLimitFromRow` have no such keys in their scope, and null is exactly right
  // for them.
  loanAmountFrom: z.number().min(0).max(100_000_000).nullable().default(null),
  loanAmountBelow: z.number().min(0).max(100_000_000).nullable().default(null),
  ficoFrom: z.number().int().min(0).max(850).nullable().default(null),
  ficoBelow: z.number().int().min(0).max(850).nullable().default(null),
  unitsFrom: z.number().int().min(1).max(500).nullable().default(null),
  unitsThrough: z.number().int().min(1).max(500).nullable().default(null),
  // A set, and a non-empty one when present. Zero codes is a band that can never match
  // any file - invisible rather than wrong, which is what the inverted-DSCR refine below
  // exists to catch - and duplicates are rejected rather than deduplicated, because a
  // codec emitting `['primary','primary']` has a bug this is the cheapest place to see.
  occupancies: z.array(enumOf(OCCUPANCY_CODES))
    .min(1).max(OCCUPANCY_CODES.length)
    .refine((v) => new Set(v).size === v.length,
      { message: 'An occupancy scope lists each occupancy once' })
    .nullable().default(null),
  unlimitedBelow: z.enum(['printed', 'inferred']),
  // Defaults to null for the same reason the selector tiers do: every band written before
  // 2026-09-04 has no such key in its stored scope, and "no printed way out of this cap"
  // is exactly true of all of them. `unlimitedBelow` still refuses a default, because
  // that one records something the lender may not have said.
  escape: z.object({
    kind: z.literal('reserves_months'),
    months: z.number().int().min(1).max(120),
    printedAs: z.string().min(1).max(2000),
  }).nullable().default(null),
  printedAs: z.string().min(1).max(2000),
}).refine(
  // A ceiling at or under the floor is a band no LTV can land in - invisible rather than
  // wrong, the same failure the inverted DSCR window below is refused for.
  (v) => v.atOrBelowLtv === null || v.atOrBelowLtv > v.aboveLtv,
  {
    message: 'An LTV band needs room in it: atOrBelowLtv must be above aboveLtv',
    path: ['atOrBelowLtv'],
  },
).refine(
  (v) => v.dscrFrom === null || v.dscrBelow === null || v.dscrFrom < v.dscrBelow,
  {
    message: 'A DSCR window needs room in it: dscrFrom must be below dscrBelow',
    path: ['dscrFrom'],
  },
).refine(
  (v) => v.loanAmountFrom === null || v.loanAmountBelow === null
    || v.loanAmountFrom < v.loanAmountBelow,
  {
    message: 'A loan-amount tier needs room in it: loanAmountFrom must be below '
      + 'loanAmountBelow',
    path: ['loanAmountFrom'],
  },
).refine(
  (v) => v.ficoFrom === null || v.ficoBelow === null || v.ficoFrom < v.ficoBelow,
  {
    message: 'A score tier needs room in it: ficoFrom must be below ficoBelow',
    path: ['ficoFrom'],
  },
).refine(
  // Inclusive at both ends, so equal is a legal one-unit tier ("4 unit: $500,000") and
  // only an inverted pair is a band that can never match anything.
  (v) => v.unitsFrom === null || v.unitsThrough === null || v.unitsFrom <= v.unitsThrough,
  {
    message: 'A unit tier needs room in it: unitsFrom must not exceed unitsThrough',
    path: ['unitsFrom'],
  },
);

/* ════════════════════════ RESERVES ════════════════════════ */

/**
 * ═══ THE FLOOR, AND WHY IT IS NOT THE CEILING ═══
 *
 * The sibling of the cash-in-hand shape above, and every judgement in here follows from
 * one difference between them: a cash-in-hand cap is a CEILING and a reserve requirement
 * is a FLOOR. That inverts three things, and getting any of them backwards produces a
 * confident wrong answer rather than a visible error.
 *
 *   - THE STRICTEST RUNG. Where two cash-in-hand bands reach a file the stricter is the
 *     LOWER dollar figure. Where two reserve rungs reach a file the stricter is the
 *     HIGHER month count. Folding these the same way would quote a borrower the easiest
 *     requirement his lender publishes.
 *   - THE DIRECTION OF DAMAGE. Reading a cap too high offers money the lender will not
 *     give; reading a floor too LOW promises a lender who would decline. Both are false
 *     yeses and both are the failure this product is arranged against.
 *   - THE CARVE-OUT DOES NOT APPLY. `mayInferUnlimitedComplement` exists because a cap
 *     printed above a line means no cap below it, which is how the trade reads silence
 *     about a ceiling. There is no equivalent for a floor. A lender who prints a
 *     requirement above 80% LTV has NOT thereby said there is none below, and inventing
 *     one would be this product guessing in the dangerous direction.
 *
 * `packages/ingest/src/reserves.ts` owns the READING - which sentences become rungs and
 * which are refused by name. This file owns the arithmetic. Neither repeats the other,
 * for the reason the cash-in-hand header gives: a second opinion about when a rule binds
 * is how the two halves start disagreeing about the same file.
 */

/** An endpoint of a printed range, with the comparison the page actually used. */
export interface Bound {
  readonly value: number;
  /** True for <= or >=, false for < or >. One character, and it decides a file. */
  readonly inclusive: boolean;
}

/**
 * One rung of a printed reserve rule.
 *
 * Every selector is a PAIR of optional bounds rather than the cash-in-hand shape's single
 * threshold, and the pages force that: a cap is always written as a ceiling above a line,
 * while reserve ladders print in both directions and in closed ranges. "LTV > 80%",
 * "CLTV <= 75%" and "$150,000 - $500,000" are all in this corpus.
 *
 * A null pair means the rung is not tiered on that axis, which is the ordinary case and
 * true of the 24 flat statements of the 61 measured.
 */
export interface ReservesRequirement {
  /** Months of PITIA the borrower must hold. Zero is a real answer - Acra prints one. */
  readonly months: number;
  readonly loanAmountMin: Bound | null;
  readonly loanAmountMax: Bound | null;
  readonly ltvMin: Bound | null;
  readonly ltvMax: Bound | null;
  readonly ficoMin: Bound | null;
  readonly ficoMax: Bound | null;
  /** Null when the rung names no purpose. Arc prints one rung per purpose. */
  readonly purposes: readonly LoanPurpose[] | null;
  /** The sentence as printed, verbatim. Every claim in this product is citable. */
  readonly printedAs: string;
}

export type ReservesOutcome = 'clears' | 'unknown' | 'binds' | 'does_not_apply';

/** What the intake could not answer, in the words a broker reads on the form. */
export type ReservesUnknownInput =
  'reservesMonths' | 'ltv' | 'loanAmount' | 'ficoScore' | 'loanPurpose';

export interface ReservesFinding {
  readonly outcome: ReservesOutcome;
  /** The months the governing rung demands. Null when no rung governs. */
  readonly requiredMonths: number | null;
  /** held minus required, when both are known. Negative when it binds - the gap. */
  readonly shortfallMonths: number | null;
  readonly missing: readonly ReservesUnknownInput[];
  /** The rung that produced this finding, for citation. Null when none governs. */
  readonly requirement: ReservesRequirement | null;
}

const NO_RESERVE_RULE: ReservesFinding = {
  outcome: 'does_not_apply', requiredMonths: null, shortfallMonths: null,
  missing: [], requirement: null,
};

export interface ReservesRequest {
  readonly loanPurpose: LoanPurpose | null;
  readonly ltv: number | null;
  readonly loanAmount: number | null;
  readonly ficoScore: number | null;
  readonly reservesMonths: number | null;
}

/** Is `value` inside the printed bounds? Null bounds do not constrain. */
function within(value: number, min: Bound | null, max: Bound | null): boolean {
  if (min !== null && !(min.inclusive ? value >= min.value : value > min.value)) return false;
  if (max !== null && !(max.inclusive ? value <= max.value : value < max.value)) return false;
  return true;
}

/**
 * Does this rung govern this file, and if so is the requirement met?
 *
 * Selectors are asked cheapest-certainty-first, the same ordering `evaluateCashInHandLimit`
 * runs on: the purpose is a dropdown the sweep already matched, the LTV is derived from
 * two figures the form requires, the loan amount cannot be null on a file that reached a
 * verdict, and the score is a box a broker may leave blank.
 *
 * A selector whose fact the file does not state is an `unknown` NAMING THAT FACT, never a
 * quiet exemption. This rung might be the one that governs and we cannot say, which is
 * the same treatment a blank cash box gets and for the same reason.
 */
export function evaluateReserves(
  rung: ReservesRequirement,
  req: ReservesRequest,
): ReservesFinding {
  const ask = (m: ReservesUnknownInput): ReservesFinding => ({
    ...NO_RESERVE_RULE, outcome: 'unknown', missing: [m],
    requiredMonths: rung.months, requirement: rung,
  });

  if (rung.purposes !== null) {
    if (req.loanPurpose === null) return ask('loanPurpose');
    if (!rung.purposes.includes(req.loanPurpose)) return NO_RESERVE_RULE;
  }
  if (rung.ltvMin !== null || rung.ltvMax !== null) {
    if (req.ltv === null) return ask('ltv');
    if (!within(req.ltv, rung.ltvMin, rung.ltvMax)) return NO_RESERVE_RULE;
  }
  if (rung.loanAmountMin !== null || rung.loanAmountMax !== null) {
    if (req.loanAmount === null) return ask('loanAmount');
    if (!within(req.loanAmount, rung.loanAmountMin, rung.loanAmountMax)) return NO_RESERVE_RULE;
  }
  if (rung.ficoMin !== null || rung.ficoMax !== null) {
    if (req.ficoScore === null) return ask('ficoScore');
    if (!within(req.ficoScore, rung.ficoMin, rung.ficoMax)) return NO_RESERVE_RULE;
  }

  // The rung governs. A published ZERO clears every borrower including one who stated
  // nothing - there is no fact left to ask for once the lender has said none are needed.
  if (rung.months === 0) {
    return {
      outcome: 'clears', requiredMonths: 0, shortfallMonths: null,
      missing: [], requirement: rung,
    };
  }
  if (req.reservesMonths === null) return ask('reservesMonths');

  const shortfallMonths = req.reservesMonths - rung.months;
  return {
    outcome: shortfallMonths < 0 ? 'binds' : 'clears',
    requiredMonths: rung.months, shortfallMonths,
    missing: [], requirement: rung,
  };
}

/**
 * Every rung a program publishes, folded to one answer.
 *
 * The order is what makes it safe, and it is the cash-in-hand order with the comparator
 * inverted. A binding requirement is reported ahead of anything else and the HARSHEST of
 * them wins - the most months, because reserves are a floor. An `unknown` outranks a
 * clean rung, because a program is not clear until the rung we could not test has been
 * tested. Only with none binding and none untestable does a `clears` come back, and it
 * reports the HIGHEST requirement the file actually met, which is the number a broker
 * has to paper.
 *
 * No rung selecting the file is `does_not_apply`, not a pass with a shrug: this lender
 * published a ladder and this borrower sits outside all of it. The parser is what makes
 * that safe - it refuses a ladder whose rungs leave a gap rather than storing the
 * readable half, so "outside every rung" means the lender wrote it that way.
 */
export function evaluateReservesAll(
  rungs: readonly ReservesRequirement[],
  req: ReservesRequest,
): ReservesFinding {
  if (rungs.length === 0) return NO_RESERVE_RULE;
  const findings = rungs.map((r) => evaluateReserves(r, req));
  const harshestFirst = (a: ReservesFinding, b: ReservesFinding): number =>
    (b.requiredMonths ?? -1) - (a.requiredMonths ?? -1);

  const binding = findings.filter((f) => f.outcome === 'binds').sort(harshestFirst);
  if (binding[0]) return binding[0];

  const unknown = findings.filter((f) => f.outcome === 'unknown').sort(harshestFirst);
  const first = unknown[0];
  if (first) {
    // One question per missing answer, not one per rung: a three-rung ladder with no
    // reserves stated wants the reserves figure once.
    return { ...first, missing: [...new Set(unknown.flatMap((f) => f.missing))] };
  }

  const clears = findings.filter((f) => f.outcome === 'clears').sort(harshestFirst);
  return clears[0] ?? NO_RESERVE_RULE;
}

/** The request, read off a scenario, so no caller wires the five fields by hand. */
export function reservesRequestOf(scenario: Scenario): ReservesRequest {
  return {
    loanPurpose: scenario.loanPurpose,
    ltv: scenario.ltv,
    loanAmount: scenario.intake.loanAmount,
    // The DERIVED score, for the reason `cashInHandRequestOf` gives: a rule that swapped
    // in a co-guarantor's score is the score the sweep read the grid with, and a rung
    // tiered at 620 has to be selected by the same number.
    ficoScore: scenario.ficoScore,
    reservesMonths: scenario.intake.reservesMonths,
  };
}

const boundSchema = z.object({
  value: z.number().min(0).max(100_000_000),
  inclusive: z.boolean(),
}).nullable().default(null);

/** The storage contract, so a stored rung reads back as the rung that was written. */
export const reservesRequirementSchema = z.object({
  months: z.number().int().min(0).max(60),
  loanAmountMin: boundSchema, loanAmountMax: boundSchema,
  ltvMin: boundSchema, ltvMax: boundSchema,
  ficoMin: boundSchema, ficoMax: boundSchema,
  purposes: z.array(enumOf(LOAN_PURPOSE_CODES)).min(1).max(LOAN_PURPOSE_CODES.length)
    .refine((v) => new Set(v).size === v.length,
      { message: 'A purpose scope lists each purpose once' })
    .nullable().default(null),
  printedAs: z.string().min(1).max(2000),
}).refine(
  // An inverted pair is a rung that can never match any file - invisible rather than
  // wrong, which is the failure the cash-in-hand refinements exist to catch.
  (v) => v.loanAmountMin === null || v.loanAmountMax === null
    || v.loanAmountMin.value <= v.loanAmountMax.value,
  { message: 'A loan band needs room in it', path: ['loanAmountMin'] },
).refine(
  (v) => v.ltvMin === null || v.ltvMax === null || v.ltvMin.value <= v.ltvMax.value,
  { message: 'An LTV band needs room in it', path: ['ltvMin'] },
).refine(
  (v) => v.ficoMin === null || v.ficoMax === null || v.ficoMin.value <= v.ficoMax.value,
  { message: 'A score band needs room in it', path: ['ficoMin'] },
);
